import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Shield, Clock, Truck, ArrowRight } from 'lucide-react';
import RelatedCategories from '../../components/catalog/RelatedCategories';
import ContactForm from '../../components/forms/ContactForm';

const products = [
  {
    id: 'kso366',
    title: 'КСО-366',
    description: 'Камеры сборные одностороннего обслуживания серии КСО-366',
    features: [
      'Номинальное напряжение 6-10 кВ',
      'Номинальный ток до 630А',
      'Ток термической стойкости 20 кА',
      'IP20',
    ],
    image: '/images/transformers/kso.webp',
  },
  {
    id: 'kso386',
    title: 'КСО-386',
    description: 'Камеры сборные одностороннего обслуживания серии КСО-386',
    features: [
      'Номинальное напряжение 6-10 кВ',
      'Номинальный ток до 1000А',
      'Ток термической стойкости 31,5 кА',
      'IP20',
    ],
    image: '/images/transformers/kso.webp',
  },
  {
    id: 'kso393',
    title: 'КСО-393',
    description: 'Камеры сборные одностороннего обслуживания серии КСО-393',
    features: [
      'Номинальное напряжение 6-10 кВ',
      'Номинальный ток до 1250А',
      'Ток термической стойкости 31,5 кА',
      'IP20',
    ],
    image: '/images/transformers/kso.webp',
  },
  {
    id: 'kso298',
    title: 'КСО-298',
    description: 'Камеры сборные одностороннего обслуживания серии КСО-298',
    features: [
      'Номинальное напряжение 6-10 кВ',
      'Номинальный ток до 630А',
      'Ток термической стойкости 20 кА',
      'IP20',
    ],
    image: '/images/transformers/kso.webp',
  },
];

const KsoPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Камеры КСО - производство и поставка | Проминнова</title>
        <meta
          name="description"
          content="Производство и поставка камер КСО для распределительных устройств 6-10 кВ. Различные типы исполнения. Доставка по России."
        />
      </Helmet>

      <div className="bg-primary-50 py-12">
        <div className="container-custom">
          <Breadcrumbs />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Камеры КСО</h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Камеры сборные одностороннего обслуживания для распределительных устройств 6-10 кВ
          </p>
        </div>
      </div>

      <div className="container-custom py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-2xl font-bold mb-6">О камерах КСО</h2>
            <p className="text-lg text-gray-600 mb-8">
              Мы производим камеры сборные одностороннего обслуживания различных серий для
              комплектации распределительных устройств напряжением 6-10 кВ.
            </p>
            <div className="space-y-6">
              <div className="flex items-start">
                <Shield className="w-5 h-5 text-primary-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-medium mb-1">Гарантия качества</h3>
                  <p className="text-gray-600">
                    Все оборудование сертифицировано и соответствует ГОСТ
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <Clock className="w-5 h-5 text-primary-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-medium mb-1">Сроки производства</h3>
                  <p className="text-gray-600">От 15 до 30 дней в зависимости от серии</p>
                </div>
              </div>
              <div className="flex items-start">
                <Truck className="w-5 h-5 text-primary-600 mt-1 mr-3" />
                <div>
                  <h3 className="font-medium mb-1">Доставка по России</h3>
                  <p className="text-gray-600">Организуем доставку в любой регион</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-xl font-semibold mb-6">Получить расчет стоимости</h3>
            <ContactForm />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {products.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="h-64 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="341"
                  height="256"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">{product.title}</h3>
                <p className="text-gray-600 mb-4">{product.description}</p>
                <ul className="space-y-2 mb-6">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/catalog/kso/${product.id}`}
                  className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700"
                >
                  Подробнее
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        <RelatedCategories currentCategory="kso" />

        <div className="prose prose-lg max-w-none">
          <h2>Камеры КСО от производителя</h2>
          <p>
            Компания «Проминнова» производит и поставляет камеры сборные одностороннего обслуживания
            различных серий для комплектации распределительных устройств напряжением 6-10 кВ. Наши
            КСО отличаются высоким качеством изготовления, надежностью и длительным сроком службы.
          </p>

          <h3>Преимущества КСО производства Проминнова:</h3>
          <ul>
            <li>Изготовление по индивидуальным требованиям заказчика</li>
            <li>Использование комплектующих ведущих производителей</li>
            <li>Полный комплект разрешительной документации</li>
            <li>Заводские испытания каждой камеры</li>
            <li>Гарантийное и постгарантийное обслуживание</li>
            <li>Техническая поддержка на всех этапах</li>
          </ul>

          <p>
            Все оборудование производится в соответствии с требованиями ГОСТ и ПУЭ, имеет
            необходимые сертификаты и разрешительную документацию. Мы обеспечиваем полный комплекс
            услуг: от проектирования до монтажа и пусконаладки оборудования.
          </p>
        </div>
      </div>
    </>
  );
};

export default KsoPage;
